import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { SUPPORTED_LANGS, type SupportedLang } from "@/i18n";
import { Globe } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const LABELS: Record<SupportedLang, string> = {
  fr: "FR",
  en: "EN",
};

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = i18n.language as SupportedLang;

  const switchTo = (lang: SupportedLang) => {
    if (lang === currentLang) return;
    i18n.changeLanguage(lang);
    trackEvent("Language Switch", { from: currentLang, to: lang });

    // Replace lang prefix in current path
    const pathParts = location.pathname.split("/");
    if (SUPPORTED_LANGS.includes(pathParts[1] as SupportedLang)) {
      pathParts[1] = lang;
    } else {
      pathParts.splice(1, 0, lang);
    }
    navigate(pathParts.join("/") + location.search, { replace: true });
  };

  return (
    <div className="inline-flex items-center gap-0.5 border border-border rounded-sm overflow-hidden">
      {SUPPORTED_LANGS.map((lang) => (
        <button
          key={lang}
          onClick={() => switchTo(lang)}
          className={`px-2 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors ${
            lang === currentLang
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary"
          }`}
          aria-label={`Switch to ${lang}`}
        >
          {LABELS[lang]}
        </button>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
